import React, { useEffect, useState } from 'react'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogTitle from '@mui/material/DialogTitle'
import {
    Box,
    CircularProgress,
    DialogContent,
    FormHelperText,
    IconButton,
    SelectChangeEvent,
} from '@mui/material'
import EditIcon from '../../../../assets/icons/Edit/Edit.svg'
import {
    requiredId,
    isNumber,
    requiredString,
    requiredArray,
    isRequiredNumber,
} from '../../../../utils/validation'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import SingleSelect from '../../../../components/muiComponents/singleSelect'
import { GeneralPromocodeState } from '../../../../features/types'
import { promocodeApi } from '../../../../features/promocode/promocode'
import MultipleSelect from '../../../../components/muiComponents/multipleSelect/MultipleSelect'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { boxHeight, error } from '../style'
import { capitalize } from '../../../../utils/functions'
import { promocodeTypes } from '../../../../utils/constants'
import DatePicker from '../../../../components/muiComponents/datePicker'
import { ui } from '../../../../utils/uiTokens'

interface Props {
    editPromocode?: GeneralPromocodeState
    editMode?: boolean
}

const AddPromocodeDialog: React.FC<Props> = ({
    editMode = false,
    editPromocode = null,
}) => {
    const { formatMessage, locale } = useIntl()
    const [open, setOpen] = useState(false)

    const [createGeneralPromocode, { isLoading: isCreating }] =
        promocodeApi.useCreateGeneralPromocodeMutation()

    const validationSchema = yup.object().shape({
        description: requiredString({ formatMessage }),
        type: requiredString({ formatMessage }),
        maxUsage: isRequiredNumber({ formatMessage }),
    })

    const promocodeTypeOptions = promocodeTypes({formatMessage})

    const promocode: GeneralPromocodeState = {
        description: "",
        promoValue: null,
        minInvoiceAmount: null,
        maxInvoiceAmount: null,
        maxUsage: null,
        expiresAt: null,
    }

    const formik = useFormik({
        initialValues: promocode,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            handleCreatePromocode()
        },
    })

    const handleCreatePromocode = async () => {
        try {
            await createGeneralPromocode({
                ...formik.values,
            }).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            handleClose()
        } catch (error) {}
    }

    const handleDateChange = (event) => {
        formik.setFieldValue('expiresAt', event)
    }

    const handleClickOpen = () => {
        setOpen(true)
        formik.setValues(promocode)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            {editMode ? (
                <IconButton
                    sx={{ background: ui.track }}
                    onClick={handleClickOpen}
                >
                    <img
                        src={EditIcon}
                        alt="edit"
                    />
                </IconButton>
            ) : (
                <Button
                    variant={'contained'}
                    onClick={handleClickOpen}
                >
                    {formatMessage({ id: 'addPromocode' })}
                </Button>
            )}

            <Dialog
                maxWidth={'lg'}
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {editMode
                        ? formatMessage({ id: 'editData' })
                        : formatMessage({ id: 'addPromocode' })}
                </DialogTitle>

                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ width: '100%', mr: 4 }}>
                                <Box
                                    sx={boxHeight}
                                    mb={14}
                                >
                                    <H6>{formatMessage({ id: 'description' })}</H6>

                                    <CustomInput
                                        id="description"
                                        name="description"
                                        placeholder={formatMessage({id: 'description'})}
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                        minRows={4}
                                        value={formik.values.description}
                                        onChange={formik.handleChange}
                                        error={Boolean(
                                            formik.errors.description,
                                        )}
                                        helperText={formik.errors.description}
                                    />
                                </Box>

                                <Box
                                    sx={boxHeight}
                                    mb={2}
                                >
                                    <H6>{formatMessage({ id: 'type' })}</H6>

                                    <SingleSelect
                                        id="type"
                                        name="type"
                                        placeholder={formatMessage({
                                            id: 'type',
                                        })}
                                        valueField='value'
                                        value={formik.values.type}
                                        handleChange={formik.handleChange}
                                        handleBlur={formik.handleBlur}
                                        handleError={Boolean(
                                            formik.errors.type,
                                        )}
                                        menuItems={promocodeTypeOptions}
                                    />

                                    <FormHelperText sx={error}>
                                        {formik.touched.type &&
                                            formik.errors.type}
                                    </FormHelperText>
                                </Box>

                                <Box
                                    sx={boxHeight}
                                    mb={2}
                                >
                                    <H6>
                                        promoValue
                                    </H6>

                                    <CustomInput
                                        id="promoValue"
                                        name="promoValue"
                                        type="number"
                                        placeholder="promoValue"
                                        sx={{ marginTop: '8px' }}
                                        fullWidth
                                        value={formik.values.promoValue}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.promoValue)}
                                        helperText={formik.errors.promoValue}
                                        margin="dense"
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ width: '100%' }}>
                                <Box
                                    sx={boxHeight}
                                    mb={2}
                                >
                                    <H6>{formatMessage({id: "minInvoiceAmount"})}</H6>

                                    <CustomInput
                                        id="minInvoiceAmount"
                                        name="minInvoiceAmount"
                                        type="number"
                                        placeholder={formatMessage({id: "minInvoiceAmount"})}
                                        sx={{ marginTop: '8px' }}
                                        fullWidth
                                        value={formik.values.minInvoiceAmount}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.minInvoiceAmount)}
                                        helperText={formik.errors.minInvoiceAmount}
                                        margin="dense"
                                    />
                                </Box>

                                <Box sx={boxHeight}>
                                    <H6>{formatMessage({id: "maxInvoiceAmount"})}</H6>

                                    <CustomInput
                                        id="maxInvoiceAmount"
                                        name="maxInvoiceAmount"
                                        type="number"
                                        placeholder={formatMessage({id: "maxInvoiceAmount"})}
                                        sx={{ marginTop: '8px' }}
                                        fullWidth
                                        value={formik.values.maxInvoiceAmount}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.maxInvoiceAmount)}
                                        helperText={formik.errors.maxInvoiceAmount}
                                        margin="dense"
                                    />
                                </Box>

                                <Box sx={boxHeight}>
                                    <H6>Қолдану санының шегі</H6>

                                    <CustomInput
                                        id="maxUsage"
                                        name="maxUsage"
                                        type="number"
                                        placeholder="maxUsage"
                                        sx={{ marginTop: '8px' }}
                                        fullWidth
                                        value={formik.values.maxUsage}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={Boolean(formik.errors.maxUsage)}
                                        helperText={formik.errors.maxUsage}
                                        margin="dense"
                                    />
                                </Box>

                                <Box sx={boxHeight}>
                                    <H6>Аяқталу күні</H6>
                                    
                                    <Box sx={{ marginTop: '8px' }}>
                                        <DatePicker
                                            label="Аяқталу күні"
                                            value={formik.values.expiresAt}
                                            handleChange={(event) => handleDateChange(event)}
                                        />
                                    </Box>
                                    
                                </Box>
                            </Box>
                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        disabled={isCreating}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isCreating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddPromocodeDialog
